﻿import React, { useEffect } from 'react';

import { handleTreeViewCheckChange, ItemRenderProps, processTreeViewItems, TreeView, TreeViewCheckChangeEvent, TreeViewCheckDescriptor, TreeViewExpandChangeEvent } from '@progress/kendo-react-treeview';
import { SanitizedQuestionStem } from 'pages/reports/components/SanitizedQuestionStem';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { Button, Col, Container } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { ReportComponent } from 'apis/ReportApi';

import 'pages/reports/components/SkillTree/styles/skill-tree.scss';

import { useSuperState } from 'hooks';

type SkillTreeProps = {
    allQuestions?: any[];
    component: ReportComponent;
    currentUserId?: number;
    generateFakeProgressBars: (countOfProgressBars: number) => JSX.Element[];
    index: number;
    isInAppTest?: boolean;
    isPrinting?: boolean;
    isSubReport?: boolean;
    loading: boolean;
    questionViewerRef?: React.MutableRefObject<any>;
    resultId?: number;
    studentProfileId?: any;
    treeData: any[];
}

type TreeNodeProps = {
    allQuestions?: any[];
    depth: number;
    expandedIds: string[];
    isInAppTest?: boolean;
    item: any;
    onToggle: (text: string) => void;
    questionViewerRef?: React.MutableRefObject<any>;
    resultId?: number;
    studentProfileId?: any;
};

export const SkillTreeLegend = () => (
    <div className="skill-tree-legend d-flex flex-wrap align-items-center gap-2 gap-md-3 py-2 px-2 bg-white w-100">
        <span className="fs-6 fw-semibold me-3 flex-shrink-0">
            <FormattedMessage defaultMessage="Key:" id="key.2998634d" />
        </span>
        <div className="d-flex align-items-center me-3 flex-shrink-0">
            <i aria-hidden="true" className="fas fa-check-circle text-success me-2 fa-lg"></i>
            <span className="fw-semibold">
                <FormattedMessage defaultMessage="Correct" id="correct.d6100cc2" />
            </span>
        </div>
        <div className="d-flex align-items-center me-3 flex-shrink-0">
            <i aria-hidden="true" className="fas fa-times-circle text-danger me-2 fa-lg"></i>
            <span className="fw-semibold">
                <FormattedMessage defaultMessage="Incorrect" id="incorrect.2a901341" />
            </span>
        </div>
        <div className="d-flex align-items-center flex-shrink-0">
            <i aria-hidden="true" className="fas fa-minus-circle text-secondary me-2 fa-lg"></i>
            <span className="fw-semibold">
                <FormattedMessage defaultMessage="Unanswered" id="unanswered.f25b4805" />
            </span>
        </div>
    </div>
);

const getAllTexts = (data: any[]): string[] => {

    const texts: string[] = [];
    data.forEach((item: any) => {

        if (item.text) texts.push(item.text);
        if (item.items) texts.push(...getAllTexts(item.items));

    });
    return texts;

};

export function SkillTree({
    allQuestions,
    component,
    generateFakeProgressBars,
    index,
    isInAppTest,
    isPrinting = false,
    isSubReport = false,
    loading,
    questionViewerRef,
    resultId,
    studentProfileId,
    treeData
}: SkillTreeProps) {

    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        checkIds: [] as string[],
        expandedIds: [] as string[]
    });

    const check: TreeViewCheckDescriptor = { idField: 'text', ids: state.checkIds };

    const setCheck = (descriptor: TreeViewCheckDescriptor) => {

        setState({ checkIds: (descriptor.ids || []) as string[] });

    };

    const expand = { idField: 'text', ids: state.expandedIds };

    const select = { idField: 'text', ids: [] as string[] };

    const onToggle = (text: string) => {

        const ids = state.expandedIds.slice();
        const idx = ids.indexOf(text);

        idx === -1 ? ids.push(text) : ids.splice(idx, 1);
        setState({ expandedIds: ids });

    };

    const onExpandChange = (event: TreeViewExpandChangeEvent) => {

        onToggle(event.item.text);

    };

    const onCheckChange = (event: TreeViewCheckChangeEvent) => {

        const settings = {
            checkChildren: false,
            checkParents: false,
            singleMode: false
        };
        setCheck(handleTreeViewCheckChange(event, check, component?.Results?.Data, settings));

    };

    const renderedComponent = (props: ItemRenderProps) => {

        switch (props.item.Type) {

            case 'Theme':
            case 'Subobjective':
            case 'Objective':
                return (
                    <>
                        {renderQuestionStatus(props.item.Value.split('(')[1].split(' ')[0], props)}
                    </>
                );
            default:
                return (
                    <>
                        <div className="data-item">
                            <div className="item-header d-flex justify-content-between">
                                <div>
                                    {renderQuestionStatus(props.item.Share, props)}
                                    {props.item.Title}
                                </div>
                                {questionViewerRef && (
                                    <div className='d-print-none'>
                                        <Button
                                            onClick={e => {

                                                const index = allQuestions?.findIndex(i => i.Title === props.item.Title) ?? -1;
                                                e.stopPropagation();
                                                questionViewerRef?.current?.create({
                                                    allQuestions,
                                                    isInAppTest,
                                                    modalTitle: props.item.Title,
                                                    objectivePassPercentage: props.item.Share,
                                                    questionId: props.item.QuestionId,
                                                    questionIndex: index,
                                                    resultId,
                                                    studentProfileId,
                                                    triggerElement: e.currentTarget
                                                });

                                            }}
                                            onKeyDown={e => {

                                                if (e.key === 'Enter' || e.key === ' ') {

                                                    e.stopPropagation();

                                                }

                                            }}
                                            aria-label={`${formatMessage({ id: 'view_question.88a88fc5', defaultMessage: 'View Question' })}: ${props.item.Title} (${props.item.QuestionId})`}
                                            className='link-underline link-underline-opacity-0 link-underline-opacity-0-hover'
                                            size='sm'
                                            title={formatMessage({ id: 'view_question.88a88fc5', defaultMessage: 'View Question' })}
                                            variant='link'
                                        >
                                            <i aria-hidden='true' className="fa-solid fa-eye fa-xl text-primary"></i>
                                        </Button>
                                    </div>
                                )}
                            </div>
                            <SanitizedQuestionStem html={props.item.Description} />
                        </div>
                    </>
                );

        }

    };

    const renderQuestionStatus = (score: string, props: ItemRenderProps) => {

        const obtained = Number(score.split('/')[0]);
        const possible = Number(score.split('/')[1]);
        let percentage = ((obtained * 1) / (possible * 1)) * 100;
        props.item.Value = props.item.Value?.replace(/\bCorrect\b/g, formatMessage({ id: 'correct.d6100cc2', defaultMessage: 'Correct' }));
        if (Number.isNaN(obtained) && Number.isNaN(possible)) {

            percentage = Number(score.split('%')[0]);

        }

        if (props.item.Type != null) {

            const variant = percentage > 79 ? 'parent-tile-passed' : percentage > 0 ? 'parent-tile-mixed' : 'parent-tile-not-taken';
            const isExpanded = state.expandedIds.includes(props.item.text);

            return (
                <div
                    className={`parent-tile ${variant}`}
                    id={props.item.id}
                >
                    <i aria-hidden='true' className={`fa-solid ${isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'} parent-tile__chevron d-print-none`} />
                    <span>
                        {props.item.text}
                        {' '}
                        {props.item.Value}
                    </span>
                </div>
            );

        } else {

            if (props.item.Answered) {

                if (percentage > 79) {

                    return (
                        <span title={formatMessage({ id: 'correct.d6100cc2', defaultMessage: 'Correct' })}>
                            <span className='visually-hidden'>
                                <FormattedMessage defaultMessage="Correct" id="correct.d6100cc2" />
                            </span>
                            <i aria-hidden='true' className='fa-solid fa-circle-check fa-xl data-item-icon is-passed' />
                        </span>
                    );

                } else {

                    return (
                        <span title={formatMessage({ id: 'incorrect.2a901341', defaultMessage: 'Incorrect' })}>
                            <span className='visually-hidden'>
                                <FormattedMessage defaultMessage="Incorrect" id="incorrect.2a901341" />
                            </span>
                            <i aria-hidden='true' className='fa-solid fa-circle-x fa-xl data-item-icon is-not-passed' />
                        </span>
                    );

                }

            } else {

                return (
                    <span title={formatMessage({ id: 'unanswered.f25b4805', defaultMessage: 'Unanswered' })}>
                        <span className='visually-hidden'>
                            <FormattedMessage defaultMessage="Unanswered" id="unanswered.f25b4805" />
                        </span>
                        <i aria-hidden='true' className='fa-solid fa-circle-minus fa-xl data-item-icon is-not-attempted' />
                    </span>
                );

            }

        }

    };

    useEffect(() => {

        if (component?.Results?.Data) {

            setState({ expandedIds: [...new Set(getAllTexts(component.Results.Data))] });

        }

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <div className="skill-tree-header row g-2 align-items-center mb-3 flex-wrap">
                <div className="col-12 col-md-auto flex-grow-1 mb-2">
                    <SkillTreeLegend />
                </div>
                <div className="col-12 col-md-auto d-flex gap-2 justify-content-md-end justify-content-start mt-2 mt-md-0">
                    <GMetrixButton
                        onClick={() => {

                            setState({ expandedIds: [...new Set(getAllTexts(component?.Results?.Data || []))] });

                        }}
                        className="mx-2 d-print-none"
                        id="expandAllObjectives"
                        size="sm"
                        variant="primary"
                    >
                        <FormattedMessage defaultMessage="Expand All" id="expand_all.d0a4e9d8" />
                    </GMetrixButton>
                    <GMetrixButton
                        onClick={() => {

                            setState({ expandedIds: [] });

                        }}
                        className="d-print-none"
                        size="sm"
                        variant="outline-primary"
                    >
                        <FormattedMessage defaultMessage="Collapse All" id="collapse_all.c1cb3a8d" />
                    </GMetrixButton>
                </div>
            </div>
            <div className="skill-tree-responsive-wrapper">
                <Col
                    style={isPrinting
                        ? { width: '100%' }
                        : { maxHeight: '80vh', overflowY: 'auto', width: '100%' }}
                    className="skill-tree p-0"
                    key={`${component.ComponentId}-${index}`}
                    sm={12}
                >
                    {!loading ? (
                        <TreeView
                            data={processTreeViewItems(treeData, {
                                check: check,
                                expand: expand,
                                select: select
                            })}
                            aria-multiselectable={true}
                            expandIcons={false}
                            item={renderedComponent}
                            onCheckChange={onCheckChange}
                            onExpandChange={onExpandChange}
                            onItemClick={onExpandChange}
                        />
                    ) : (
                        <Container>
                            {generateFakeProgressBars(4)}
                        </Container>
                    )}
                </Col>
            </div>
        </>
    );

}
